<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  topics: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['edit']);
const { t } = useI18n();

const laudemioPoints = computed(() => [
  t('POST_SALE.TOPICS.ATTENTION_POINTS.LAUDEMIO.ENTRY'),
  t('POST_SALE.TOPICS.ATTENTION_POINTS.LAUDEMIO.FORUM'),
  t('POST_SALE.TOPICS.ATTENTION_POINTS.LAUDEMIO.FORUM_BILL'),
  t('POST_SALE.TOPICS.ATTENTION_POINTS.LAUDEMIO.LAUDEMIO_BILL'),
  t('POST_SALE.TOPICS.ATTENTION_POINTS.LAUDEMIO.ALVARA'),
]);

const points = computed(() =>
  props.topics.flatMap(topic => {
    const dynamicPoints = (topic.attention_points || []).map(point => ({
      tag: point.tag,
      text: point.text,
      topic,
    }));
    if (topic.topic_code !== 'laudemio') return dynamicPoints;

    return [
      ...dynamicPoints,
      ...laudemioPoints.value.map(text => ({
        tag: t('POST_SALE.TOPICS.ATTENTION_POINTS.LAUDEMIO.TAG'),
        text,
        topic,
      })),
    ];
  })
);
</script>

<template>
  <LaisCard
    data-testid="post-sale-attention-points"
    class="w-full max-w-[867px]"
    density="flush"
  >
    <template #header>
      <h2 class="text-xl font-bold leading-6 text-lais-text font-redHatDisplay">
        {{ t('POST_SALE.TOPICS.ATTENTION_POINTS.TITLE') }}
      </h2>
    </template>
    <div v-if="loading" class="bg-lais-bg-soft">
      <div
        v-for="index in 5"
        :key="index"
        class="flex h-15 flex-col items-start gap-2 border-b border-lais-border-soft bg-white px-3 py-3"
      >
        <span class="h-4 w-12 animate-pulse rounded-lais-sm bg-lais-bg-soft" />
        <span
          class="h-4 animate-pulse rounded-lais-sm bg-lais-bg-soft"
          :class="index % 2 ? 'w-1/4' : 'w-2/5'"
        />
      </div>
    </div>
    <div
      v-else-if="points.length"
      data-testid="post-sale-attention-list"
      class="bg-lais-bg-soft pb-9"
    >
      <article
        v-for="(point, index) in points"
        :key="`${point.topic.id}-${index}`"
        data-testid="post-sale-attention-point"
        class="group flex flex-col gap-3 border-b border-x-[3px] border-white bg-white px-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:hover:border-x-lais-primary-200 sm:hover:bg-lais-bg-soft"
      >
        <div class="min-w-0">
          <span
            data-testid="post-sale-attention-tag"
            class="inline-flex rounded-full bg-lais-warning/15 px-2 py-1 text-xs font-semibold text-lais-warning"
          >
            {{ point.tag }}
          </span>
          <p class="mt-2 break-words px-1 text-sm text-lais-text-body">
            {{ point.text }}
          </p>
        </div>
        <LaisPrimaryButton
          data-testid="post-sale-attention-details"
          class="self-start sm:hidden sm:group-hover:inline-flex sm:group-focus-within:inline-flex sm:self-auto"
          variant="link"
          size="sm"
          @click="emit('edit', point.topic)"
        >
          {{ t('POST_SALE.TOPICS.ATTENTION_POINTS.VIEW_DETAILS') }}
        </LaisPrimaryButton>
      </article>
    </div>
  </LaisCard>
</template>
